// ArChat — notificación de sistema para mensaje entrante (pestaña en segundo plano / sin foco).
// Título: remitente si el payload trae nombre/nick; cuerpo: texto o etiqueta Foto/Audio/Video/Archivo.

const shownMessageIds = new Set();
const MAX_IDS = 400;

function trimShownIds() {
  if (shownMessageIds.size <= MAX_IDS) return;
  const arr = [...shownMessageIds];
  const drop = arr.slice(0, Math.max(0, arr.length - MAX_IDS));
  drop.forEach((id) => shownMessageIds.delete(id));
}

/**
 * Indica si conviene avisar por el SO (pestaña minimizada, otra ventana con foco, PWA en background).
 * @returns {boolean}
 */
export function shouldOfferSystemIncomingNotification() {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState === 'hidden') return true;
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return true;
  return false;
}

/**
 * Primer string no vacío.
 * @param {...unknown} values
 * @returns {string}
 */
function firstNonEmpty(...values) {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).replace(/\s+/g, ' ').trim();
    if (s !== '' && s !== 'undefined' && s !== 'null' && s !== '[object Object]') return s;
  }
  return '';
}

/**
 * Texto plano seguro para notificación (sin HTML rico).
 * @param {string} s
 * @param {number} max
 * @returns {string}
 */
function stripForNotification(s, max) {
  const t = String(s || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return '';
  if (t.length <= max) return t;
  return `${t.slice(0, max)}\u2026`;
}

/**
 * Cuerpo de notificación según tipo y texto.
 * @param {Record<string, unknown>|null|undefined} raw
 * @returns {string}
 */
function buildNotificationBody(raw) {
  if (!raw || typeof raw !== 'object') return 'Nuevo mensaje';
  const tipo = String(
    raw.tipo ?? raw.tipo_mensaje ?? raw.message_type ?? raw.type ?? ''
  )
    .toLowerCase()
    .trim();
  const text = String(
    raw.text ?? raw.mensaje ?? raw.display_text ?? raw.contenido ?? raw.contenido_original ?? raw.body ?? ''
  ).trim();

  if (tipo.includes('imagen') || tipo === 'image' || tipo === 'photo' || tipo === 'picture' || tipo === 'foto') {
    return text ? stripForNotification(text, 160) : 'Foto';
  }
  if (tipo.includes('audio') || tipo === 'voice' || tipo === 'voz') {
    return text ? stripForNotification(text, 160) : 'Audio';
  }
  if (tipo.includes('video')) {
    return text ? stripForNotification(text, 160) : 'Video';
  }
  if (tipo.includes('archivo') || tipo === 'file' || tipo === 'document' || tipo === 'adjunto' || tipo === 'attachment') {
    return text ? stripForNotification(text, 160) : 'Archivo';
  }
  if (text) return stripForNotification(text, 200);
  return 'Nuevo mensaje';
}

/**
 * Construye título, cuerpo e ids para Notification API a partir del payload WS (o normalizado).
 *
 * @param {Record<string, unknown>|null|undefined} raw
 * @returns {{ title: string, body: string, conversationId: string, messageId: string }}
 */
export function buildIncomingMessageNotificationPayload(raw) {
  if (!raw || typeof raw !== 'object') {
    return { title: 'ArChat', body: 'Nuevo mensaje', conversationId: '', messageId: '' };
  }
  const conversationId = String(
    raw.conversation_id ?? raw.conversacion_id ?? raw.conversationId ?? raw.cid ?? ''
  ).trim();
  const messageId = String(
    raw.id ?? raw.message_id ?? raw.mensaje_id ?? raw.messageId ?? raw.temp_id ?? ''
  ).trim();

  const title = stripForNotification(
    firstNonEmpty(
      raw.alias,
      raw.contact_alias,
      raw.sender_display_name,
      raw.remitente_nick,
      raw.emisor_nick,
      raw.sender_nick,
      raw.nick,
      raw.sender_name,
      raw.sender_username,
      raw.username,
      raw.nombre,
      raw.nombre_real,
      raw.nombre_display,
      raw.from_user_name,
      raw.remitente_nombre,
      raw.contacto_nombre,
      raw.user_nick,
      raw.senderNick,
      raw.displayName
    ) || 'ArChat',
    72
  );

  const body = buildNotificationBody(raw);

  return { title, body, conversationId, messageId };
}

/**
 * Muestra notificación local (SW si existe; si no, Notification de página en contexto seguro).
 *
 * @param {object} opts
 * @param {string} opts.conversationId
 * @param {string} opts.messageId
 * @param {string} [opts.title]
 * @param {string} [opts.body]
 * @returns {Promise<void>}
 */
export async function showLocalIncomingMessageNotification({ conversationId, messageId, title, body }) {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (!messageId || !conversationId) return;
  if (shownMessageIds.has(String(messageId))) return;
  shownMessageIds.add(String(messageId));
  trimShownIds();

  const cid = String(conversationId).trim();
  const tag = `archat-conv-${cid}`;
  const t = title && String(title).trim() !== '' ? String(title) : 'ArChat';
  const b = body && String(body).trim() !== '' ? String(body) : 'Nuevo mensaje';

  const data = {
    archat: {
      type: 'local_incoming',
      conversation_id: cid,
      message_id: String(messageId),
    },
  };

  const icon = typeof window.location !== 'undefined' ? `${window.location.origin}/logoarchat.png` : '/logoarchat.png';

  const options = {
    body: b,
    tag,
    renotify: true,
    data,
    icon,
    badge: icon,
  };

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready.catch(() => null);
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(t, options);
        return;
      }
    }
    if (window.isSecureContext && typeof Notification === 'function') {
      try {
        void new Notification(t, options);
      } catch (_) {
        /* noop */
      }
    }
  } catch (_) {
    /* noop */
  }
}

/**
 * Limpia deduplicación (tests / diagnóstico).
 */
export function __resetSystemIncomingNotificationDedupeForTests() {
  shownMessageIds.clear();
}
